import { defineStore } from "pinia";
import { parseISO, format, eachMonthOfInterval, startOfMonth } from "date-fns";
import { nanoid } from "nanoid";
import { TAXONOMIES } from "@facet/utils/constants";
import { useMediaQuery } from "@vueuse/core";

function initTaxonomyState() {
    return TAXONOMIES.reduce((acc, { key }) => {
        acc[key] = [];
        return acc;
    }, {});
}

function parseConfig() {
    const appElement = document.querySelector("#app");
    const configString = appElement?.dataset?.config;

    if (!configString) {
        return {
            event_type: null,
            options: [],
            current_month: null,
        };
    }

    try {
        return JSON.parse(configString);
    } catch (e) {
        console.error("Failed to parse data-config:", e);
        return {
            event_type: null,
            options: [],
            current_month: null,
        };
    }
}

export const useFeedStore = defineStore("facetStore", {
    state: () => {
        const config = parseConfig();

        return {
            // Config
            layout: "grid", // grid/list
            currentPage: 1,
            animationBuffer: 333,
            monthRange: [],
            singleTaxMode: config.event_type !== null,
            eventType: config.event_type,
            showFilters: config.options?.includes("filters") ?? false,
            showMonthNav: config.options?.includes("months") ?? false,
            showTimes: config.options?.includes("times") ?? false,
            currentMonthOnly: config.options?.includes("current_month") ?? false,
            ignoreExclusions: config.options?.includes("ignore_exclusions") ?? false,

            // Data
            items: [],
            filteredItems: [],
            groupedItems: {},

            total: 0,
            filters: initTaxonomyState(),
            facets: initTaxonomyState(),
            month: format(new Date(), "yyyy-MM"),
            currentMonth: null, // for doing intersection observer stuff
            search: "",

            // Conditionals
            isLoading: false,
            isToggled: false,
            isMobile: useMediaQuery("(max-width: 639px)"),
        };
    },

    actions: {
        init() {
            TAXONOMIES.forEach(({ key }) => {
                if (!(key in this.filters)) {
                    this.filters[key] = [];
                }
                if (!(key in this.facets)) {
                    this.facets[key] = [];
                }
            });
        },

        async fetchItems() {
            this.isLoading = true;

            const params = new URLSearchParams();

            if (this.singleTaxMode && this.eventType) {
                params.set("event_type", this.eventType);
            }

            if (this.currentMonthOnly) {
                params.set("month", this.month);
            }

            if (this.ignoreExclusions) {
                params.set("ignore_exclusions", true);
            }

            this.items = [];
            try {
                const res = await fetch(`/wp-json/albert-hall/v1/whats-on/?${params.toString()}`);
                if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);

                const data = await res.json();

                this.facets.event_type = data.filters.event_type;

                setTimeout(() => {
                    this.items =
                        data.data.map((item) => ({
                            ...item,
                            __key: `${nanoid(10)}__${item.ID}`,
                        })) || [];
                    this.filteredItems = this.items;
                    this.groupedItems = this.groupEventsByYearMonth(this.items);
                    this.monthRange = this.getMonthRange(this.groupedItems, this.items);
                    if (this.hasFacetFilters) {
                        this.applyFilters();
                    }
                }, this.animationBuffer);

                this.total = data.meta.total;
            } catch (err) {
                console.error("Failed to fetch items:", err);
            } finally {
                this.isLoading = false;
            }
        },

        async setMonthAndFetch(month) {
            this.month = month;
            await this.fetchItems();
        },

        applyFilters() {
            let result = [...this.items];

            // iterate through all active filters
            Object.entries(this.filters).forEach(([taxonomy, selected]) => {
                if (selected && selected.length) {
                    result = result.filter((post) => {
                        const terms = post[taxonomy]; // e.g. post.event_type
                        if (!terms) return false;
                        return terms.some((t) => selected.includes(t.term_id));
                    });
                }
            });

            this.filteredItems = result;
            this.groupedItems = this.groupEventsByYearMonth(this.filteredItems);
            this.monthRange = this.getMonthRange(this.groupedItems, this.items);
        },

        getMonthRange(groupedItems, events) {
            if (!events.length) return [];

            // 1. Find min + max dates
            const sorted = [...events].sort((a, b) => new Date(a.event_start) - new Date(b.event_start));
            const start = startOfMonth(parseISO(sorted[0].event_start));
            const end = startOfMonth(parseISO(sorted[sorted.length - 1].event_start));

            // 2. Generate all months in range
            const months = eachMonthOfInterval({ start, end }).map((d) => ({
                year: Number(format(d, "yyyy")),
                value: format(d, "yyyy-MM"),
                label: format(d, "MMMM"),
                count: 0,
            }));

            // 3. Reduce into year groups and attach counts
            return months.reduce((acc, m) => {
                let group = acc.find((g) => g.year === m.year);
                if (!group) {
                    group = { year: m.year, items: [] };
                    acc.push(group);
                }

                const yearGroup = groupedItems.find((g) => g.year === String(m.year));
                const monthGroup = yearGroup?.months.find((month) => month.value === m.value);

                group.items.push({
                    value: m.value,
                    label: m.label,
                    count: monthGroup ? monthGroup.items.length : 0,
                });

                return acc;
            }, []);
        },

        groupEventsByYearMonth(events) {
            const sorted = [...events].sort((a, b) => new Date(a.event_start) - new Date(b.event_start));

            return sorted.reduce((years, event) => {
                if (!event.event_start) return years; // skip if missing

                const date = parseISO(event.event_start); // e.g. "2025-10-17T19:00:00"
                const year = format(date, "yyyy");
                const monthValue = format(date, "yyyy-MM");
                const monthLabel = format(date, "MMMM");

                // year group
                let yearGroup = years.find((y) => y.year === year);
                if (!yearGroup) {
                    yearGroup = { year, months: [] };
                    years.push(yearGroup);
                }

                // month group
                let monthGroup = yearGroup.months.find((m) => m.value === monthValue);
                if (!monthGroup) {
                    monthGroup = { label: monthLabel, value: monthValue, items: [] };
                    yearGroup.months.push(monthGroup);
                }

                monthGroup.items.push(event);
                return years;
            }, []);
        },

        setCurrentMonth() {
            const now = new Date();
            const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
            this.month = month;
        },

        async clearFilters() {
            this.isLoading = true;
            Object.keys(this.filters).forEach((key) => {
                this.filters[key] = [];
            });
            this.setCurrentMonth();
            this.isToggled = false;
            this.search = "";
            this.isLoading = false;
        },
    },
    getters: {
        showReset: (state) => {
            return state.hasFacetFilters || state.search !== "" || state.month !== format(new Date(), "yyyy-MM");
        },

        hasNoResults: (state) => {
            return !state.isLoading && state.total === 0;
        },

        hasFacetFilters: (state) => {
            return Object.values(state.filters).some((arr) => Array.isArray(arr) && arr.length > 0);
        },

        hasMorePages: (state) => {
            return state.page < state.last_page;
        },

        showPagination: (state) => {
            return !state.isLoading && !state.hasNoResults && state.hasMorePages;
        },
    },
});
